import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import { styled } from '@mui/material/styles';
import { AppBar, Alert, Badge, Box, ButtonBase, Stack, Toolbar, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// import { loadCaptchaEnginge } from 'react-simple-captcha';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import LoginSignupDialog from './visitor/LoginSignupDialog';
import ForgotPasswordDialog from './forgotpassword/ForgotPasswordDialog';
import ResetPasswordDialog from './forgotpassword/ResetPasswordDialog';
import { useDispatch, useSelector } from '../../../redux/store';
import { openLoginSignup } from '../../../redux/slices/authPages';
import LanguagePopover from './LanguagePopover';

import Image from '../../../components/Image';
import useLocales from '../../../hooks/useLocales';
import palette from '../../../theme/palette';
import { ThemeButton, ThemeButtonPrimary } from '../../GlobalStyles';
import useAuth from '../../../hooks/useAuth';
import NotificationPopover from './NotificationPopover';
import { useLangNavigate } from '../../../hooks/useLangNavigate';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ theme, showDownloadApp, isDesktop }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  background: palette.header.bgColor,
  // borderTop: `1px solid ${palette.header.border}`,
  // borderBottom: `1px solid ${palette.header.border}`,
  height: 60,
  top: showDownloadApp && !isDesktop ? 36 : 0,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  // [theme.breakpoints.up('lg')]: {
  //   height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
  //   width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
  //   ...(isCollapse && {
  //     width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
  //   }),
  //   ...(isOffset && {
  //     height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
  //   }),
  //   ...(verticalLayout && {
  //     width: '100%',
  //     height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
  //     backgroundColor: theme.palette.background.default,
  //   }),
  // },
}));

const BalanceDepositBox = styled(Stack)(({ theme, isDesktop }) => ({
  minHeight: isDesktop ? 46 : 'unset',
  cursor: 'pointer',
  background: '#295538',
  color: '#fff',
  borderRadius: '.5rem',
  paddingLeft: 10,
  alignItems: 'center',
}));

const DepositBox = styled(Stack)(({ theme, isDesktop }) => ({
  background: 'linear-gradient(90deg, #DD8545 0%, #AE5300 100%)',
  alignItems: 'center',
  boxShadow: '0 4px 8px rgba(29,34,37,.1)',
  borderRadius: '.5rem',
  padding: isDesktop ? '11px 10px' : '8px 10px',
}));

// const DownloadBox = styled(Box)(({ theme }) => ({
//   minHeight: '46px',
//   cursor: 'pointer',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   background: 'linear-gradient(180deg, #e5b92a 0%, #c8910e 100%)',
//   borderRadius: '.5rem',
//   padding: '11px 10px',
//   minWidth: '46px',
// }));

const ProfileBox = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  borderRadius: 8,
  overflow: 'hidden',
  padding: '3px 3px 3px 15px',
  background: '#295538',
  cursor: 'pointer',
}));

const ProfileNameText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: '#fff',
  fontWeight: 700,
  maxWidth: 50,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
}));

const DownloadAppAlert = styled(Alert)(({ theme }) => ({
  fontSize: 9,
  fontWeight: 400,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 0,
  padding: '0px 16px',
  position: 'fixed',
  top: 0,
  zIndex: theme.zIndex.appBar + 1,
  width: '100%',
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  onToggleCollapse: PropTypes.func,
  openAccountDrawer: PropTypes.bool,
  setOpenAccountDrawer: PropTypes.func,
};

export default function DashboardHeader({
  isOpenSidebar = false,
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
  // onCloseSidebar,
  onToggleCollapse,
  openAccountDrawer,
  setOpenAccountDrawer,
  showDownloadApp,
  setShowDownloadApp,
}) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const { isAuthenticated, logout } = useAuth();

  const isDesktop = useResponsive('up', 'lg');

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const { isOpenLoginSignupDialog } = useSelector((x) => x.authPages);

  const { memberBalance, unreadInboxCount, userProfile } = useSelector((x) => x.lookup);

  const [openForgotPasswordDialog, setOpenForgotPasswordDialog] = useState(false);

  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);

  const navigate = useLangNavigate();

  const [showWallet, setShowWallet] = useState(true);

  useEffect(() => {
    // if (localStorage.getItem('referralCode') && !localStorage.getItem('accessToken')) {
    //   setTimeout(() => {
    //     dispatch(openLoginSignup({ open: true, isLogin: false }));
    //   }, 1000);
    // }
    // Check if got resetpassword token
    if (localStorage.getItem('resetToken')) {
      setOpenResetPasswordDialog(true);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      // getProfile();
    } else {
      console.log('loggedoff');
    }
  }, [localStorage.getItem('accessToken')]);

  const balanceDisplay = useMemo(() => {
    const rawBalance = memberBalance?.displayBalance !== '0' ? memberBalance?.displayBalance : '0.00';
    if (showWallet) {
      return rawBalance;
    }

    return rawBalance.toString().replace(/\d/g, '*');
  }, [showWallet, memberBalance]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <>
      {showDownloadApp && !isDesktop && (
        <DownloadAppAlert
          icon={<Iconify icon={'line-md:download-loop'} sx={{ color: '#fff' }} />}
          variant="filled"
          severity="success"
          sx={{
            '.MuiAlert-message': {
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            },
          }}
        >
          <Box
            onClick={() => {
              navigate('/download');
              window.location.reload();
            }}
            sx={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <p style={{ whiteSpace: 'nowrap', fontSize: 9 }}>{translate('download_message')}</p>
            <ButtonBase
              sx={{
                ml: 1,
                border: 1,
                background: '#fff',
                color: '#2D9923',
                borderRadius: '4px',
                p: '2px 4px',
                fontSize: '9px',
                fontWeight: 600,
              }}
            >
              {translate('download')}
            </ButtonBase>
          </Box>
          <Iconify
            onClick={() => setShowDownloadApp(false)}
            icon={'ic:round-close'}
            sx={{ color: '#fff', width: '20px', height: '20px', cursor: 'pointer' }}
          />
        </DownloadAppAlert>
      )}

      <RootStyle
        isCollapse={isCollapse}
        isOffset={isOffset}
        verticalLayout={verticalLayout}
        showDownloadApp={showDownloadApp}
        isDesktop={isDesktop}
      >
        <Toolbar
          sx={{
            minHeight: '100% !important',
            // pl: { lg: 1 },
            // pr: { lg: 2 },
            p: '10px 20px',
            alignItems: 'center',
          }}
        >
          {/* {isDesktop && verticalLayout && } */}

          {isDesktop && (
            <Box sx={{ flex: { xs: 1, lg: 0 } }} onClick={onOpenSidebar}>
              <Iconify
                icon={'heroicons-solid:menu-alt-1'}
                sx={{ color: '#2D9923', width: '30px', height: '30px', cursor: 'pointer' }}
              />
            </Box>
          )}
          <Logo sx={{ ml: { md: 2.5, sm: 0, xs: 0 } }} />

          {/* {!isDesktop && (
          <Stack direction="row" alignItems={'center'} justifyContent="center" sx={{ flex: { xs: 1, lg: 0 } }}>
            <Box sx={{ width: { sm: '5rem', md: '7rem' } }} onClick={() => navigate('/')}>
              <Image
                src={require('../../../assets/brand-logo.png')}
                alt=""
                sx={{ cursor: 'pointer', width: '140px', height: '30px' }}
              />
            </Box>
          </Stack>
        )} */}

          <Stack direction={'row'} spacing={'10px'} flex={1} justifyContent={'end'} alignItems={'center'}>
            {isDesktop && !localStorage.getItem('accessToken') && (
              <>
                <LoadingButton
                  variant="text"
                  sx={{
                    padding: '5px 20px',
                    fontSize: '14px',
                    color: '#FFCD1A',
                  }}
                  onClick={() => dispatch(openLoginSignup({ open: true, isLogin: true }))}
                >
                  {translate('login')}
                </LoadingButton>
                <ThemeButtonPrimary onClick={() => navigate('/auth/register')}>
                  {translate('register')}
                </ThemeButtonPrimary>
              </>
            )}
            {localStorage.getItem('accessToken') && (
              <BalanceDepositBox
                isDesktop={isDesktop}
                direction={'row'}
                spacing={1}
                onClick={() => navigate('/user/wallet')}
              >
                <Stack>
                  <Typography sx={{ color: '#fff', fontSize: '.8rem', fontWeight: 700 }}>
                    {translate('balance')}:
                  </Typography>
                  <Typography sx={{ color: '#fff', fontSize: '.8rem', fontWeight: 700 }}>{balanceDisplay}</Typography>
                </Stack>
                <Iconify icon={'mingcute:refresh-2-fill'} sx={{ width: '15px', color: '#fff' }} />
                <DepositBox isDesktop={isDesktop} direction={'row'} spacing={'5px'}>
                  <Image
                    src={require('../../../assets/home/money-deposit.png')}
                    sx={{ width: '20px', height: '20px' }}
                  />
                  <Typography
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', display: isDesktop ? 'block' : 'none' }}
                  >
                    {translate('deposit')}
                  </Typography>
                </DepositBox>
              </BalanceDepositBox>
            )}
            {isDesktop && localStorage.getItem('accessToken') && (
              <>
                <ProfileBox
                  // onClick={() => setOpenAccountDrawer(true)}
                  onClick={() => navigate('/user/setting')}
                  direction="row"
                  spacing={'5px'}
                >
                  <Stack>
                    <ProfileNameText>{userProfile?.username}</ProfileNameText>
                    <Typography sx={{ fontSize: '12px', color: '#fff', fontWeight: 300 }}>
                      {userProfile?.MemberGroup?.name}
                    </Typography>
                  </Stack>
                  <Iconify icon={'ri:arrow-right-s-line'} sx={{ color: '#fff', height: '20px', width: '20px' }} />
                </ProfileBox>
                <NotificationPopover />
              </>
            )}

            {/* <DownloadBox>
            <Image
              src={require('../../../assets/home/android.png')}
              sx={{ width: '25px', height: '25px', cursor: 'pointer' }}
            />
          </DownloadBox> */}
            <LanguagePopover isDesktop={isDesktop} />
            {!isDesktop && localStorage.getItem('accessToken') && (
              <Badge badgeContent={unreadInboxCount || 0} color="error" variant="dot">
                <Iconify
                  onClick={onOpenSidebar}
                  icon={'material-symbols:menu-rounded'}
                  sx={{ color: '#2D9923', width: '24px', height: '24px', cursor: 'pointer' }}
                />
              </Badge>
            )}
          </Stack>

          {isOpenLoginSignupDialog.open && (
            <LoginSignupDialog setOpenForgotPasswordDialog={setOpenForgotPasswordDialog} />
          )}

          {openForgotPasswordDialog && (
            <ForgotPasswordDialog
              openForgotPasswordDialog={openForgotPasswordDialog}
              setOpenForgotPasswordDialog={setOpenForgotPasswordDialog}
            />
          )}

          {openResetPasswordDialog && (
            <ResetPasswordDialog
              openResetPasswordDialog={openResetPasswordDialog}
              setOpenResetPasswordDialog={setOpenResetPasswordDialog}
              setOpenLoginSignup={(params) => dispatch(openLoginSignup(params))}
            />
          )}
        </Toolbar>
      </RootStyle>
    </>
  );
}
